import to from 'await-to-js';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Avatar, Box, Card, Link, Typography } from '@mui/material';
import type { PageConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import { useCommonTranslation } from 'c-translation';
import { PermissionName } from 'c-sdk';
import type { UsageTimelineItem, UsageUser } from 'c-sdk';
import { usageIcon } from 'c-main/icons';
import { useAPIClientRequest, useDateUtils } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import { LoadingSpinner } from 'c-components';
import MuiRouterLink from 'c-components/MuiRouterLink';
import { NetworkRequestState } from 'c-data-layer';
import {
    LoadMore,
    PageStack,
    PanelRow,
    PeriodSelect,
    SidePanel,
    SideSection,
    SummaryCell,
    SummaryStrip,
    UsageEmpty,
    UsageLoadError,
} from './UsageParts';
import {
    DEFAULT_PERIOD_DAYS,
    organisationLink,
    percentOf,
    periodFor,
    useFormatDuration,
} from './usageLib';
import UsageTimeline, { CampaignRef } from './UsageTimeline';
import ActivityGrid from './ActivityGrid';
import { DevicesContent } from './UsageDevices';
import { pageLabel, useActivityLabel } from './activityLabels';

/**
 * One person's use of the platform, as designed on the usage canvas (board 3): who they are and
 * when they were last here, the headline numbers in one strip, their activity as Day › Session ›
 * Page visit, and one side panel of reference facts.
 */

/** Log rows per request. One busy page visit can be dozens of rows, so ask for the API's most. */
const PER_PAGE = 100;
// Within this, "last seen" gets a green dot: they are probably still around.
const RECENT_MS = 15 * 60 * 1000;

const initials = (name: string) =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0]?.toUpperCase())
        .join('');

const PersonHeader = ({ usage, controls }: { usage: UsageUser; controls: React.ReactNode }) => {
    const t = useCommonTranslation();
    const { formatDateString, formatTimeAgo } = useDateUtils();
    const { user } = usage;
    const usual = usage.devices?.browsers[0];
    const recent =
        user.last_seen != null && Date.now() - new Date(user.last_seen).getTime() < RECENT_MS;
    const subline = [
        user.first_seen
            ? t('Modules.Admin.Usage.firstSeen', {
                  date: formatDateString({ date: user.first_seen, removeTime: true }),
              })
            : null,
        usual
            ? t('Modules.Admin.Usage.usualBrowser', {
                  device: usual.os
                      ? t('Modules.Admin.Usage.deviceOn', { browser: usual.browser, os: usual.os })
                      : usual.browser,
              })
            : null,
    ].filter(Boolean);

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'center',
                justifyContent: 'space-between',
            }}
        >
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', minWidth: 0 }}>
                <Avatar
                    aria-hidden="true"
                    sx={{
                        width: 52,
                        height: 52,
                        bgcolor: 'primary.main',
                        fontSize: 18,
                        fontWeight: 600,
                    }}
                >
                    {initials(user.name)}
                </Avatar>
                <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}
                    >
                        <Typography
                            component="h1"
                            sx={{ m: 0, fontSize: 24, fontWeight: 600, lineHeight: 1.2 }}
                        >
                            {user.name}
                        </Typography>
                        {user.last_seen && (
                            <Box
                                component="span"
                                title={formatDateString({
                                    date: user.last_seen,
                                    removeTime: false,
                                    timeFormat: 'HH:mm',
                                })}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    px: 1.25,
                                    py: 0.375,
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 999,
                                    bgcolor: 'background.paper',
                                    typography: 'caption',
                                    fontWeight: 500,
                                }}
                            >
                                <Box
                                    aria-hidden="true"
                                    sx={{
                                        width: 7,
                                        height: 7,
                                        borderRadius: '50%',
                                        bgcolor: recent ? 'success.main' : 'text.disabled',
                                    }}
                                />
                                {t('Modules.Admin.Usage.lastSeen', {
                                    date: formatTimeAgo(user.last_seen),
                                })}
                            </Box>
                        )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        {[
                            user.role ? t(`Modules.Admin.Usage.types.${user.role}`) : null,
                            user.organisation ? (
                                <MuiRouterLink
                                    key="organisation"
                                    to={organisationLink(
                                        user.organisation.type,
                                        user.organisation.id,
                                    )}
                                    underline="hover"
                                >
                                    {user.organisation.name ?? `#${user.organisation.id}`}
                                </MuiRouterLink>
                            ) : null,
                            user.email,
                        ]
                            .filter(Boolean)
                            .map((part, index) => (
                                // eslint-disable-next-line react/no-array-index-key -- a fixed, ordered line
                                <React.Fragment key={index}>
                                    {index > 0 && ' · '}
                                    {part}
                                </React.Fragment>
                            ))}
                    </Typography>
                    {subline.length > 0 && (
                        <Typography variant="caption" color="text.disabled">
                            {subline.join(' · ')}
                        </Typography>
                    )}
                </Box>
            </Box>
            {controls}
        </Box>
    );
};

const UsageUserPage = () => {
    const t = useCommonTranslation();
    const formatDuration = useFormatDuration();
    const activityLabel = useActivityLabel();
    const params = useParams<{ id: string }>();
    const [days, setDays] = useState(DEFAULT_PERIOD_DAYS);
    const period = useMemo(() => periodFor(days), [days]);
    const { start, data, requestState } = useAPIClientRequest(ApiClient.Usage.user);
    // Later pages of the timeline load as the reader scrolls, on their own request so a failure
    // there leaves the page standing.
    const moreRequest = useAPIClientRequest(ApiClient.Usage.user);
    const { start: startMore } = moreRequest;
    const [more, setMore] = useState<UsageTimelineItem[]>([]);
    const [pagesLoaded, setPagesLoaded] = useState(1);
    // Bumped when the person or period changes, so a late page from before is dropped.
    const generation = useRef(0);

    useEffect(() => {
        generation.current += 1;
        setMore([]);
        setPagesLoaded(1);
        to(start(Number(params.id), { ...period, page: 1, per_page: PER_PAGE }));
    }, [params.id, period, start]);

    const usage = data?.data?.data;
    const total = usage?.timeline.meta.total ?? 0;
    const hasMore = pagesLoaded * PER_PAGE < total;
    const loadingMore = moreRequest.requestState === NetworkRequestState.InProgress;

    const loadMore = useCallback(async () => {
        if (loadingMore || !hasMore) return;
        const asked = generation.current;
        const [, response] = await to(
            startMore(Number(params.id), { ...period, page: pagesLoaded + 1, per_page: PER_PAGE }),
        );
        const rows = response?.data?.data.timeline.data;
        if (!rows || asked !== generation.current) return;
        setMore(previous => [...previous, ...rows]);
        setPagesLoaded(loaded => loaded + 1);
    }, [loadingMore, hasMore, startMore, params.id, period, pagesLoaded]);

    // New activity shifts the pages along, so a row can come back twice: keep the first.
    const timelineRows = useMemo(() => {
        const seen = new Set<number>();
        return [...(usage?.timeline.data ?? []), ...more].filter(row => {
            if (seen.has(row.id)) return false;
            seen.add(row.id);
            return true;
        });
    }, [usage, more]);

    // Pages by their own name, so two admin pages don't both read "Admin". Pages with no time are
    // left out; two templates with the same name are added together.
    const topPages = useMemo(() => {
        const byLabel = new Map<string, number>();
        (usage?.pages ?? []).forEach(p => {
            if (p.time_ms <= 0) return;
            const label = pageLabel(p.name);
            byLabel.set(label, (byLabel.get(label) ?? 0) + p.time_ms);
        });
        return [...byLabel.entries()]
            .map(([label, time_ms]) => ({ label, time_ms }))
            .sort((a, b) => b.time_ms - a.time_ms)
            .slice(0, 6);
    }, [usage]);
    const maxPageTime = Math.max(1, ...topPages.map(p => p.time_ms));

    const clarityProject = import.meta.env.VITE_CLARITY_PROJECT_ID;
    const controls = (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {clarityProject && usage && (
                <Link
                    href={`https://clarity.microsoft.com/projects/view/${clarityProject}/impressions`}
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                    variant="body2"
                    fontWeight={500}
                    title={t('Modules.Admin.Usage.watchRecordingsHint', { id: usage.user.id })}
                    sx={{ px: 1 }}
                >
                    {t('Modules.Admin.Usage.watchRecordings')}
                </Link>
            )}
            <PeriodSelect value={days} onChange={setDays} />
        </Box>
    );

    const topCampaign = usage?.campaigns[0];
    const sessionsPerDay =
        usage && usage.totals.active_days > 0
            ? (usage.totals.sessions / usage.totals.active_days).toFixed(1)
            : null;

    return (
        <PageStack>
            {usage ? (
                <PersonHeader usage={usage} controls={controls} />
            ) : (
                <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                    <Typography component="h1" variant="h5" fontWeight={600}>
                        {t('Modules.Admin.Usage.userTitle')}
                    </Typography>
                    {controls}
                </Box>
            )}

            {requestState === NetworkRequestState.Error && <UsageLoadError />}
            {requestState === NetworkRequestState.InProgress && !usage && <LoadingSpinner />}

            {usage && (
                <>
                    <SummaryStrip>
                        <SummaryCell
                            label={t('Modules.Admin.Usage.kpi.activeDays')}
                            value={usage.totals.active_days}
                            note={t('Modules.Admin.Usage.kpiNotes.activeDaysOf', { count: days })}
                        />
                        <SummaryCell
                            label={t('Modules.Admin.Usage.kpi.sessions')}
                            value={usage.totals.sessions}
                            note={
                                sessionsPerDay
                                    ? t('Modules.Admin.Usage.kpiNotes.perActiveDay', {
                                          count: Number(sessionsPerDay),
                                      })
                                    : undefined
                            }
                        />
                        <SummaryCell
                            label={t('Modules.Admin.Usage.kpi.timeInPortal')}
                            value={formatDuration(usage.totals.time_ms)}
                            note={
                                usage.totals.sessions > 0
                                    ? t('Modules.Admin.Usage.kpiNotes.avgPerSession', {
                                          time: formatDuration(
                                              usage.totals.time_ms / usage.totals.sessions,
                                          ),
                                      })
                                    : undefined
                            }
                        />
                        <SummaryCell
                            label={t('Modules.Admin.Usage.kpi.campaignsViewed')}
                            value={usage.totals.campaigns_viewed}
                            note={
                                topCampaign
                                    ? t('Modules.Admin.Usage.kpiNotes.mostly', {
                                          id: topCampaign.id,
                                      })
                                    : undefined
                            }
                        />
                        <SummaryCell
                            label={t('Modules.Admin.Usage.actionsTitle')}
                            value={usage.totals.events}
                            note={
                                usage.events.length > 0
                                    ? usage.events
                                          .slice(0, 2)
                                          .map(
                                              event =>
                                                  `${t(
                                                      `Modules.Admin.Usage.eventsShort.${event.name}`,
                                                      {
                                                          defaultValue: activityLabel({
                                                              kind: 'event',
                                                              name: event.name,
                                                          }),
                                                      },
                                                  )} ${event.count}`,
                                          )
                                          .join(' · ')
                                    : undefined
                            }
                        />
                    </SummaryStrip>

                    <PanelRow>
                        <Card variant="outlined" sx={{ minWidth: 0 }}>
                            <Box
                                component="section"
                                aria-label={t('Modules.Admin.Usage.recentActivity')}
                                sx={{
                                    px: { xs: 2, md: 3 },
                                    pt: { xs: 2, md: 2.75 },
                                    pb: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.75,
                                }}
                            >
                                <Typography
                                    component="h2"
                                    sx={{ m: 0, fontSize: 17, fontWeight: 600 }}
                                >
                                    {t('Modules.Admin.Usage.recentActivity')}
                                </Typography>
                                {usage.timeline.data.length === 0 && <UsageEmpty />}
                                <UsageTimeline rows={timelineRows} />
                                {hasMore && (
                                    <LoadMore
                                        loading={loadingMore}
                                        failed={
                                            moreRequest.requestState === NetworkRequestState.Error
                                        }
                                        onLoad={loadMore}
                                        // A fresh watch after each page, in case the end is still in view.
                                        watchKey={pagesLoaded}
                                    />
                                )}
                            </Box>
                        </Card>

                        <SidePanel>
                            <SideSection title={t('Modules.Admin.Usage.activityGrid')}>
                                <ActivityGrid grid={usage.grid} />
                            </SideSection>
                            <SideSection title={t('Modules.Admin.Usage.campaignsMost')}>
                                {usage.campaigns.length === 0 && <UsageEmpty />}
                                {usage.campaigns.map(campaign => (
                                    <Box
                                        key={campaign.id}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'minmax(0, 1fr) auto',
                                            gap: 1.5,
                                            alignItems: 'baseline',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                minWidth: 0,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            <CampaignRef id={campaign.id} name={campaign.name} />
                                        </Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                fontVariantNumeric: 'tabular-nums',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {t('Modules.Admin.Usage.visitsCount', {
                                                count: campaign.visits,
                                            })}
                                        </Typography>
                                    </Box>
                                ))}
                            </SideSection>
                            <SideSection title={t('Modules.Admin.Usage.pagesTitle')}>
                                {topPages.length === 0 && <UsageEmpty />}
                                {topPages.map(p => (
                                    <Box
                                        key={p.label}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'minmax(0, 1fr) auto',
                                            gap: '4px 12px',
                                            alignItems: 'baseline',
                                        }}
                                    >
                                        <Typography variant="body2" noWrap title={p.label}>
                                            {p.label}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                fontVariantNumeric: 'tabular-nums',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {formatDuration(p.time_ms)}
                                        </Typography>
                                        <Box
                                            role="img"
                                            aria-label={`${p.label} ${percentOf(
                                                p.time_ms,
                                                maxPageTime,
                                            )}%`}
                                            sx={{
                                                gridColumn: '1 / -1',
                                                height: 4,
                                                borderRadius: 999,
                                                bgcolor: 'action.hover',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    height: 4,
                                                    borderRadius: 999,
                                                    bgcolor: 'primary.main',
                                                    width: `${percentOf(p.time_ms, maxPageTime)}%`,
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                ))}
                            </SideSection>
                            {usage.events.length > 0 && (
                                <SideSection title={t('Modules.Admin.Usage.actionsTitle')}>
                                    {usage.events.map(event => (
                                        <Box
                                            key={event.name}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                gap: 1,
                                            }}
                                        >
                                            <Typography variant="body2">
                                                {activityLabel({ kind: 'event', name: event.name })}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                fontWeight={600}
                                                sx={{ fontVariantNumeric: 'tabular-nums' }}
                                            >
                                                {event.count}
                                            </Typography>
                                        </Box>
                                    ))}
                                </SideSection>
                            )}
                            <SideSection title={t('Modules.Admin.Usage.devicesTitle')}>
                                <DevicesContent
                                    devices={usage.devices ?? { types: [], browsers: [] }}
                                />
                            </SideSection>
                        </SidePanel>
                    </PanelRow>
                </>
            )}
        </PageStack>
    );
};

const setup: PageConfig = {
    id: 'UsageUserPage',
    component: UsageUserPage,
    systemSearch: {
        title: 'Modules.Admin.Usage.userTitle',
        description: 'Modules.Admin.Usage.description',
        type: 'page',
        saveAsRecentPage: false,
        aliases: undefined,
        icon: usageIcon,
        route: PostAuthRoutes.Admin.UsageUser,
        breadcrumbs: [
            { label: 'Modules.Admin.Home.metaTitle', link: PostAuthRoutes.Admin.AdminHome },
            { label: 'Modules.Admin.Usage.metaTitle', link: PostAuthRoutes.Admin.UsageOverview },
            { label: 'Modules.Admin.Usage.userTitle' },
        ],
        permissions: [PermissionName.AdminCan_view_usage],
    },
};

export default setup;
